import { useMemo, useState } from "react";

import {
    Alert,
    Box,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Stack,
} from "@mui/material";
import { useGetDossiersCapabilitiesQuery } from "services/streamingApi/caseManager.api";
import { DossierCapabilityName } from "types/DossierCapabilityName";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export interface DossierCapabilitySelectorProps {
    defaultValue: DossierCapabilityName[];
    disabled?: boolean;
    isNewSelection: boolean;
    onChange: (newValue: DossierCapabilityName[]) => void;
}

export function DossierCapabilitySelector(
    props: DossierCapabilitySelectorProps,
) {
    const {
        data: dossierCapabilities,
        isFetching,
        isError,
    } = useGetDossiersCapabilitiesQuery(undefined);

    const [selectedDossierCapabilityNames, setSelectedDossierCapabilityNames] =
        useState<DossierCapabilityName[]>(props.defaultValue);

    const handleChange = (
        event: SelectChangeEvent<DossierCapabilityName[]>,
    ) => {
        if (!dossierCapabilities) return;

        const newValue = Array.isArray(event.target.value)
            ? event.target.value
            : [event.target.value as DossierCapabilityName];

        setSelectedDossierCapabilityNames(newValue);

        props.onChange(newValue);
    };

    const dossierCapabilityWarnings = useMemo(() => {
        return dossierCapabilities?.filter(
            (dc) =>
                selectedDossierCapabilityNames.includes(dc.name) &&
                dc.userWarning,
        );
    }, [dossierCapabilities, selectedDossierCapabilityNames]);

    const placeholderMessage = isError
        ? "Could not load dossier capabilities"
        : isFetching
          ? "Loading dossier capabilities"
          : undefined;

    return (
        <>
            <FormControl
                margin="dense"
                fullWidth
            >
                <InputLabel>Capabilities</InputLabel>
                <Select
                    fullWidth
                    multiple
                    error={isError}
                    margin="dense"
                    disabled={props.disabled}
                    value={selectedDossierCapabilityNames}
                    onChange={handleChange}
                    input={<OutlinedInput label="Capabilities" />}
                    renderValue={(selected) => {
                        if (placeholderMessage) {
                            return <em>{placeholderMessage}</em>;
                        }

                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                }}
                            >
                                {selected.map((value) => (
                                    <Chip
                                        key={value}
                                        label={
                                            dossierCapabilities?.find(
                                                (s) => s.name === value,
                                            )?.displayName
                                        }
                                    />
                                ))}
                            </Box>
                        );
                    }}
                    MenuProps={MenuProps}
                >
                    {placeholderMessage && (
                        <MenuItem
                            disabled
                            value=""
                        >
                            <em>{placeholderMessage}</em>
                        </MenuItem>
                    )}

                    {dossierCapabilities?.map((dc) => (
                        <MenuItem
                            key={dc.name}
                            value={dc.name}
                            disabled={
                                !(
                                    props.isNewSelection ||
                                    (!props.defaultValue.some(
                                        (s) => s === dc.name,
                                    ) &&
                                        dc.canBeEnabledPostCreation === true)
                                )
                            }
                        >
                            <Checkbox
                                checked={selectedDossierCapabilityNames.some(
                                    (s) => s === dc.name,
                                )}
                            />
                            <ListItemText primary={dc.displayName} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {dossierCapabilityWarnings && (
                <Stack
                    direction={"column"}
                    spacing={1}
                >
                    {dossierCapabilityWarnings.map((x) => (
                        <Alert
                            severity="warning"
                            key={`userwarning_${x.name}`}
                        >
                            <strong>{x.displayName}:</strong> {x.userWarning}
                        </Alert>
                    ))}
                </Stack>
            )}
        </>
    );
}
